import React, { useState } from 'react';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import * as jsPDF from 'jspdf';
import { Plus, Trash2, Send, CheckCircle, Download } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { format } from 'date-fns';
import { CalendarComponent } from '../components/CustomCalendar';

const Packages = () => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [events, setEvents] = useState([
    {
      id: 1,
      eventType: '',
      eventDate: null,
      services: [],
    },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const packages = [
    {
      id: 'silver',
      name: 'Silver Package',
      price: 45000,
      displayPrice: 'Starting from ₹45,000',
      features: [
        '4 Hours Coverage',
        '200+ Edited Photos',
        '1 Photographer',
        'Online Gallery',
        'Basic Retouching',
        '2 Weeks Delivery',
      ],
      color: 'bg-gray-100',
    },
    {
      id: 'gold',
      name: 'Gold Package',
      price: 85000,
      displayPrice: 'Starting from ₹85,000',
      features: [
        '8 Hours Coverage',
        '400+ Edited Photos',
        '2 Photographers',
        'Online Gallery',
        'Advanced Retouching',
        'Cinematic Highlights Video',
        '1 Week Delivery',
        'Printed Photo Album',
      ],
      color: 'bg-gray-900',
      popular: true,
    },
    {
      id: 'platinum',
      name: 'Platinum Package',
      price: 125000,
      displayPrice: 'Starting from ₹1,25,000',
      features: [
        '2-Day Coverage',
        '100+ Edited Photos',
        '2 Photographers + 2 Videographer',
        'Printed Album',
        'Premium Retouching',
        'Full Length Wedding Film',
        'Cinematic Highlights Video',
        '10-15 Days Delivery',
        'Drone Coverage',
        'Pre-wedding Shoot',
      ],
      color: 'bg-gray-100',
    },
  ];

  const eventTypes = [
    { value: 'wedding', label: 'Wedding' },
    { value: 'engagement', label: 'Engagement' },
    { value: 'birthday', label: 'Birthday' },
    { value: 'anniversary', label: 'Anniversary' },
    { value: 'haldi', label: 'Haldi' },
    { value: 'mehndi', label: 'Mehndi' },
    { value: 'sangeet', label: 'Sangeet' },
    { value: 'reception', label: 'Reception' },
    { value: 'corporate', label: 'Corporate Event' },
    { value: 'fashion', label: 'Fashion Shoot' },
    { value: 'other', label: 'Other' },
  ];

  const availableServices = [
    { id: 'photography', label: 'Photography' },
    { id: 'videography', label: 'Videography' },
    { id: 'drone', label: 'Drone Coverage' },
    { id: 'album', label: 'Printed Album' },
    { id: 'highlights', label: 'Cinematic Highlights' },
    { id: 'candid', label: 'Candid Photography' },
    { id: 'traditional', label: 'Traditional Photography' },
    { id: 'prewedding', label: 'Pre-wedding Shoot' },
    { id: 'makeup', label: 'Makeup Artist' },
    { id: 'decoration', label: 'Event Decoration' },
    { id: 'editedphotos', label: 'Edited Photos' },
    { id: 'reels', label: 'Reels' },
    { id: 'einvitation', label: 'E-Invitation' },
    { id: 'mehndiartist', label: 'Mehndi Artist' },
  ];

  const handleContactChange = e => {
    const { name, value } = e.target;
    setContactInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleEventChange = (id, field, value) => {
    setEvents(prev =>
      prev.map(event =>
        event.id === id ? { ...event, [field]: value } : event
      )
    );
  };

  const handleServiceToggle = (eventId, serviceId) => {
    setEvents(prev =>
      prev.map(event => {
        if (event.id === eventId) {
          const services = event.services.includes(serviceId)
            ? event.services.filter(s => s !== serviceId)
            : [...event.services, serviceId];
          return { ...event, services };
        }
        return event;
      })
    );
  };

  const addEvent = () => {
    setEvents(prev => [
      ...prev,
      {
        id: Date.now(),
        eventType: '',
        eventDate: null,
        services: [],
      },
    ]);
  };

  const removeEvent = id => {
    if (events.length > 1) {
      setEvents(prev => prev.filter(event => event.id !== id));
    }
  };

  const validateForm = () => {
    if (!contactInfo.name || !contactInfo.email || !contactInfo.phone) {
      alert('Please fill in all contact information');
      return false;
    }

    for (let event of events) {
      if (!event.eventType || !event.eventDate) {
        alert('Please select event type and date for all events');
        return false;
      }
      if (event.services.length === 0) {
        alert('Please select at least one service for each event');
        return false;
      }
    }

    return true;
  };

  const generatePDF = () => {
    if (!selectedPackage) {
      alert('Please select a package first');
      return;
    }

    const selectedPkg = packages.find(p => p.id === selectedPackage);
    const doc = new jsPDF.jsPDF();

    // Editable company information
    const companyInfo = {
      name: 'FRAMES BY GAURAV',
      tagline: 'Professional Wedding & Event Photography',
      address: 'Shop No. 4, Sujanpura Crossing, Alambagh, Lucknow, UP-226005',
      phone: '+91 73984 13465',
      email: 'info@framesbygaurav.com',
      website: 'www.framesbygaurav.com',
    };

    // Page 1: Logo and Company Name
    // Add logo placeholder (you can replace this with actual image)
    doc.setFillColor(0, 0, 0); // Black background for logo area
    doc.rect(70, 30, 70, 40, 'F');

    // Logo text (replace with actual image if available)
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('LOGO', 105, 55, { align: 'center' });

    // Company name (editable)
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(companyInfo.name, 105, 90, { align: 'center' });

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(companyInfo.tagline, 105, 105, { align: 'center' });

    // Quotation title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('PHOTOGRAPHY QUOTATION', 105, 130, { align: 'center' });

    // Date and client info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 160);
    doc.text(`Quotation For: ${contactInfo.name || 'Client'}`, 20, 175);
    doc.text(`Email: ${contactInfo.email || 'Not provided'}`, 20, 190);
    doc.text(`Phone: ${contactInfo.phone || 'Not provided'}`, 20, 205);

    // Page 2: Services and Details
    doc.addPage();

    // Header with company info
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, 210, 40, 'F');

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(companyInfo.name, 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(companyInfo.address, 105, 30, { align: 'center' });
    doc.text(`${companyInfo.phone} | ${companyInfo.email}`, 105, 35, {
      align: 'center',
    });

    // Reset text color
    doc.setTextColor(0, 0, 0);

    // Package Information
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('PACKAGE DETAILS', 20, 60);

    doc.setLineWidth(0.5);
    doc.line(20, 65, 190, 65);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Selected Package:', 20, 80);
    doc.setFont('helvetica', 'normal');
    doc.text(selectedPkg.name, 80, 80);

    doc.setFont('helvetica', 'bold');
    doc.text('Package Price:', 20, 95);
    doc.setFont('helvetica', 'normal');
    doc.text(`₹${selectedPkg.price.toLocaleString()}`, 80, 95);

    // Events Section
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('EVENT DETAILS', 20, 115);

    doc.setLineWidth(0.5);
    doc.line(20, 120, 190, 120);

    let yPosition = 135;
    events.forEach((event, index) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 30;
      }

      // Event header with background
      doc.setFillColor(240, 240, 240);
      doc.rect(20, yPosition - 5, 170, 15, 'F');

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const eventType =
        eventTypes.find(et => et.value === event.eventType)?.label ||
        event.eventType;
      doc.text(`${index + 1}. ${eventType}`, 25, yPosition + 5);

      yPosition += 20;

      // Event details
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const eventDate = event.eventDate
        ? format(event.eventDate, 'PPP')
        : 'Date not selected';
      doc.text(`📅 Date: ${eventDate}`, 30, yPosition);

      yPosition += 10;

      // Services
      doc.setFont('helvetica', 'bold');
      doc.text('📸 Services:', 30, yPosition);
      doc.setFont('helvetica', 'normal');

      const services = event.services
        .map(sid => availableServices.find(s => s.id === sid)?.label)
        .filter(Boolean)
        .join(', ');

      if (services) {
        const splitServices = doc.splitTextToSize(services, 140);
        doc.text(splitServices, 50, yPosition);
        yPosition += splitServices.length * 5 + 5;
      } else {
        doc.text('No services selected', 50, yPosition);
        yPosition += 10;
      }

      yPosition += 10; // Extra space between events
    });

    // Page 3: Terms and Conditions
    doc.addPage();

    // Header
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, 210, 30, 'F');

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('TERMS & CONDITIONS', 105, 20, { align: 'center' });

    // Reset text color
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const terms = [
      '1. This quotation is valid for 30 days from the date of issue.',
      '2. 50% advance payment is required to confirm the booking.',
      '3. Remaining 50% payment to be made before the event.',
      '4. All payments are non-refundable once services are rendered.',
      '5. Additional charges may apply for travel outside Lucknow city limits.',
      '6. Raw photos will be delivered within 15-20 days after the event.',
      '7. Edited photos and albums will be delivered within 30-45 days.',
      '8. Client is responsible for providing necessary permissions for photography.',
      '9. Frames by Gaurav reserves the right to use photos for promotional purposes.',
      '10. Any changes to the package must be communicated 7 days before the event.',
      '11. Force majeure conditions may affect service delivery timelines.',
      '12. All copyrights of photographs remain with Frames by Gaurav.',
      '',
      'For any queries, please contact us at:',
      `Phone: ${companyInfo.phone}`,
      `Email: ${companyInfo.email}`,
      `Website: ${companyInfo.website}`,
    ];

    let termsY = 45;
    terms.forEach(term => {
      if (termsY > 270) {
        doc.addPage();
        termsY = 30;
      }

      if (term === '') {
        termsY += 5;
      } else {
        const splitTerm = doc.splitTextToSize(term, 170);
        doc.text(splitTerm, 20, termsY);
        termsY += splitTerm.length * 5 + 3;
      }
    });

    // Save the PDF
    const fileName = `Frames_by_Gaurav_Quotation_${contactInfo.name || 'Client'}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!selectedPackage) {
      alert('Please select a package first');
      return;
    }

    setIsSubmitting(true);

    // Prepare data for submission
    const selectedPkg = packages.find(p => p.id === selectedPackage);
    if (!selectedPkg) {
      alert('Selected package not found');
      setIsSubmitting(false);
      return;
    }

    const bookingData = {
      package: selectedPkg.name,
      contact: contactInfo,
      events: events.map(event => ({
        type:
          eventTypes.find(et => et.value === event.eventType)?.label ||
          event.eventType,
        date: event.eventDate ? format(event.eventDate, 'PPP') : 'Not selected',
        services: event.services
          .map(sid => availableServices.find(s => s.id === sid)?.label)
          .join(', '),
      })),
    };

    // Create WhatsApp message
    let whatsappMessage = `*New Booking Inquiry - Frames by Gaurav*\n\n`;
    whatsappMessage += `*Package:* ${bookingData.package}\n\n`;
    whatsappMessage += `*Contact Details:*\n`;
    whatsappMessage += `Name: ${contactInfo.name}\n`;
    whatsappMessage += `Email: ${contactInfo.email}\n`;
    whatsappMessage += `Phone: ${contactInfo.phone}\n\n`;
    whatsappMessage += `*Events:*\n`;

    bookingData.events.forEach((event, index) => {
      whatsappMessage += `${index + 1}. ${event.type}\n`;
      whatsappMessage += `   Date: ${event.date}\n`;
      whatsappMessage += `   Services: ${event.services}\n\n`;
    });

    // Mock submission (in real scenario, this would call backend API)
    console.log('Booking Data:', bookingData);
    console.log('WhatsApp message will be sent to: +91 73984 13465');

    // Open WhatsApp immediately
    const whatsappUrl = `https://wa.me/917398413465?text=${encodeURIComponent(whatsappMessage)}`;

    try {
      // Try window.open first
      const newWindow = window.open(whatsappUrl, '_blank');
      if (
        !newWindow ||
        newWindow.closed ||
        typeof newWindow.closed === 'undefined'
      ) {
        // If popup blocked, try alternative methods
        console.warn('Popup blocked, trying alternative method');
        // Create a temporary link and click it
        const link = document.createElement('a');
        link.href = whatsappUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
      alert(
        'Could not open WhatsApp. Please copy this link and open it manually: ' +
          whatsappUrl
      );
    }

    // Show success state
    setIsSubmitting(false);
    setSubmitSuccess(true);

    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitSuccess(false);
      setSelectedPackage(null);
      setContactInfo({ name: '', email: '', phone: '' });
      setEvents([{ id: 1, eventType: '', eventDate: null, services: [] }]);
    }, 3000);
  };

  return (
    <>
      <SEO
        title="Photography Packages in Lucknow | Frames by Gaurav"
        description="Wedding and event photography packages in Lucknow start at ₹25,000. Compare Silver, Gold and Platinum packages and check your date."
        keywords="wedding photography packages lucknow, photography pricing, wedding photographer rates, photography packages india, affordable wedding photography, frames by gaurav packages"
        canonicalUrl="/packages"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ServiceOffer',
          name: 'Photography Packages',
          description:
            'Professional photography packages for weddings, events and special occasions',
          provider: {
            '@type': 'ProfessionalService',
            name: 'Frames by Gaurav',
          },
          offers: [
            {
              '@type': 'Offer',
              name: 'Silver Package',
              priceSpecification: {
                '@type': 'PriceSpecification',
                priceCurrency: 'INR',
                price: '25000',
              },
            },
          ],
        }}
      />
      <Layout>
        {/* Packages Overview */}
        {/* CTA */}
        {!selectedPackage && (
          <section className="py-20 bg-black text-white">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-5xl font-bold mb-4 tracking-tight">
                Select a Package to Get Started
              </h2>
              <p className="text-xl text-gray-400">
                Choose from our Silver, Gold, or Platinum packages and customize
                your booking
              </p>
            </div>
          </section>
        )}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-bold text-center mb-6 tracking-tight">
              Our Packages
            </h1>
            <p className="text-center text-gray-600 text-lg mb-16 max-w-2xl mx-auto">
              Choose the perfect package for your special moments and customize
              it with multiple events
            </p>

            {/* Package Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
              {packages.map(pkg => (
                <div
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedPackage(pkg.id);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Select ${pkg.name} package`}
                  className={`relative p-8 border-2 cursor-pointer transition-all duration-300 ${
                    selectedPackage === pkg.id
                      ? 'border-black scale-105'
                      : 'border-gray-300 hover:border-gray-500'
                  } ${pkg.id === 'gold' ? 'text-white' : 'text-black'} ${pkg.color}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-black text-white px-4 py-1 text-xs tracking-widest uppercase">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-2 tracking-tight">
                    {pkg.name}
                  </h3>
                  <p
                    className={`text-xl mb-6 ${pkg.id === 'gold' ? 'text-gray-300' : 'text-gray-600'}`}
                  >
                    {pkg.displayPrice}
                  </p>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle size={16} className="mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {selectedPackage === pkg.id && (
                    <div className="absolute top-4 right-4">
                      <CheckCircle
                        size={24}
                        className={
                          pkg.id === 'gold' ? 'text-white' : 'text-black'
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Booking Form */}
        {selectedPackage && (
          <section className="py-20 bg-gray-50">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-4xl font-bold text-center mb-12 tracking-tight">
                Get Your Quotation
              </h2>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Contact Information */}
                <div className="bg-white p-8 border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-6 tracking-tight">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-semibold uppercase tracking-wider mb-2"
                      >
                        Name *
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={contactInfo.name}
                        onChange={handleContactChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 focus:border-black focus:outline-none transition-colors"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-semibold uppercase tracking-wider mb-2"
                      >
                        Email *
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={contactInfo.email}
                        onChange={handleContactChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 focus:border-black focus:outline-none transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-phone"
                        className="block text-sm font-semibold uppercase tracking-wider mb-2"
                      >
                        Phone *
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        value={contactInfo.phone}
                        onChange={handleContactChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 focus:border-black focus:outline-none transition-colors"
                        placeholder="+91 7398413465"
                      />
                    </div>
                  </div>
                </div>

                {/* Events */}
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-2xl font-bold tracking-tight">
                      Event Details
                    </h3>
                  </div>

                  {events.map((event, index) => (
                    <div
                      key={event.id}
                      className="bg-white p-8 border-2 border-gray-200 relative"
                    >
                      <div className="flex justify-between items-center">
                        <h4 className="text-lg font-bold mb-6 uppercase tracking-wider">
                          Event {index + 1}
                        </h4>
                        {events.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeEvent(event.id)}
                            className="text-gray-400 hover:text-black transition-colors"
                            aria-label="Remove event"
                          >
                            <Trash2 size={20} />
                          </button>
                        )}
                      </div>

                      {/* Event Type & Date */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                          <label
                            htmlFor={`event-type-${event.id}`}
                            className="block text-sm font-semibold uppercase tracking-wider mb-2"
                          >
                            Event Type *
                          </label>
                          <Select
                            value={event.eventType}
                            onValueChange={value =>
                              handleEventChange(event.id, 'eventType', value)
                            }
                          >
                            <SelectTrigger
                              id={`event-type-${event.id}`}
                              className="w-full h-12 border-2 border-gray-300 focus:border-black"
                            >
                              <SelectValue placeholder="Select event type" />
                            </SelectTrigger>
                            <SelectContent>
                              {eventTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label
                            htmlFor={`event-date-${event.id}`}
                            className="block text-sm font-semibold uppercase tracking-wider mb-2"
                          >
                            Event Date *
                          </label>
                          <Popover open={isCalendarOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                id={`event-date-${event.id}`}
                                type="button"
                                variant="outline"
                                className="w-full h-12 justify-start text-left font-normal border-2 border-gray-300 hover:border-black"
                                onClick={() =>
                                  setIsCalendarOpen(!isCalendarOpen)
                                }
                              >
                                {event.eventDate ? (
                                  format(event.eventDate, 'PPP')
                                ) : (
                                  <span>Pick a date</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <CalendarComponent
                                selectedDate={event.eventDate}
                                onDateSelect={date => {
                                  handleEventChange(
                                    event.id,
                                    'eventDate',
                                    date
                                  );
                                  setIsCalendarOpen(false);
                                }}
                                eventId={event.id}
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      {/* Services */}
                      <fieldset>
                        <legend className="block text-sm font-semibold uppercase tracking-wider mb-4">
                          Select Services *
                        </legend>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {availableServices.map(service => (
                            <div
                              key={service.id}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`${event.id}-${service.id}`}
                                checked={event.services.includes(service.id)}
                                onCheckedChange={() =>
                                  handleServiceToggle(event.id, service.id)
                                }
                              />
                              <label
                                htmlFor={`${event.id}-${service.id}`}
                                className="text-sm cursor-pointer select-none"
                              >
                                {service.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </fieldset>
                    </div>
                  ))}
                </div>

                {/* Add More Events Button */}
                <div className="flex justify-end pt-4">
                  <button
                    type="button"
                    onClick={addEvent}
                    className="flex items-center bg-black text-white px-4 py-2 text-sm tracking-wider uppercase hover:bg-gray-900 transition-colors"
                  >
                    <Plus size={18} className="mr-2" />
                    Add More Events
                  </button>
                </div>

                {/* Submit Buttons */}
                <div className="text-center pt-4 space-y-4">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      type="button"
                      onClick={generatePDF}
                      disabled={!selectedPackage}
                      className="bg-gray-800 text-white px-8 py-4 text-sm tracking-widest uppercase hover:bg-gray-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center zoom-hover"
                    >
                      <Download className="mr-2" size={20} />
                      Download PDF Quotation
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting || submitSuccess}
                      className="bg-black text-white px-12 py-4 text-sm tracking-widest uppercase hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed inline-flex items-center zoom-hover"
                    >
                      {isSubmitting ? (
                        'Submitting...'
                      ) : submitSuccess ? (
                        <>
                          <CheckCircle className="mr-2" size={20} />
                          Sent Successfully!
                        </>
                      ) : (
                        <>
                          <Send className="mr-2" size={20} />
                          Get Quotation
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {submitSuccess && (
                  <div className="text-center p-6 bg-black text-white">
                    <p className="text-lg font-semibold mb-2">
                      Thank you for your inquiry!
                    </p>
                    <p className="text-sm">
                      Your request has been sent via WhatsApp. We&apos;ll get
                      back to you with a detailed quotation shortly.
                    </p>
                  </div>
                )}
              </form>
            </div>
          </section>
        )}
      </Layout>
    </>
  );
};

export default Packages;
